import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * KeyValueList / KeyValue — a hairline-ruled description list for evidence,
 * identity and configuration facts.
 *
 * Use it wherever a panel would otherwise stack "label: value" paragraphs or
 * nest a card per fact. Labels are quiet sans; values carry the weight. Set
 * `mono` on values that are identifiers, digests, versions or addresses so
 * they read as data of record (`.impeccable.md` — "mono carries authority").
 *
 * Layout: label column + value column from `sm` up; label stacked over value
 * below `sm`. `columns={2}` flows pairs into two columns from `lg` up.
 */

export interface KeyValueListProps extends React.HTMLAttributes<HTMLDListElement> {
  columns?: 1 | 2;
  /** Width of the label column from `sm` up. */
  labelWidth?: 'narrow' | 'default' | 'wide';
}

const labelWidths: Record<NonNullable<KeyValueListProps['labelWidth']>, string> = {
  narrow: 'sm:grid-cols-[minmax(6rem,8rem)_minmax(0,1fr)]',
  default: 'sm:grid-cols-[minmax(8rem,11rem)_minmax(0,1fr)]',
  wide: 'sm:grid-cols-[minmax(10rem,15rem)_minmax(0,1fr)]',
};

const KeyValueLabelWidthContext = React.createContext<string>(labelWidths.default);

const KeyValueList = React.forwardRef<HTMLDListElement, KeyValueListProps>(
  ({ className, columns = 1, labelWidth = 'default', ...props }, ref) => (
    <KeyValueLabelWidthContext.Provider value={labelWidths[labelWidth]}>
      <dl
        ref={ref}
        data-slot="key-value-list"
        className={cn(
          'grid grid-cols-1 text-sm',
          columns === 2 && 'lg:grid-cols-2 lg:gap-x-8',
          className,
        )}
        {...props}
      />
    </KeyValueLabelWidthContext.Provider>
  ),
);
KeyValueList.displayName = 'KeyValueList';

export interface KeyValueProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Render the value in the mono face with tabular numerals. */
  mono?: boolean;
  /** Secondary line under the value (provenance, unit, explanation). */
  hint?: React.ReactNode;
  /** Status tone applied to the value text only. */
  tone?: 'default' | 'muted' | 'danger' | 'success';
}

const valueTones: Record<NonNullable<KeyValueProps['tone']>, string> = {
  default: 'text-[var(--color-foreground)]',
  muted: 'text-[var(--color-foreground-3)]',
  danger: 'text-[var(--color-danger)]',
  success: 'text-[var(--color-success)]',
};

const KeyValue = React.forwardRef<HTMLDivElement, KeyValueProps>(
  ({ className, label, children, mono = false, hint, tone = 'default', ...props }, ref) => {
    const columns = React.useContext(KeyValueLabelWidthContext);
    return (
      <div
        ref={ref}
        data-slot="key-value"
        className={cn(
          'grid grid-cols-1 gap-x-4 gap-y-0.5 border-b border-[var(--color-hairline)] py-1.5',
          columns,
          className,
        )}
        {...props}
      >
        <dt className="text-[var(--color-foreground-3)]">{label}</dt>
        <dd
          className={cn(
            'min-w-0 break-words',
            valueTones[tone],
            mono && 'font-mono text-[0.8125rem] tabular-nums',
          )}
        >
          {children}
          {hint ? (
            <span className="mt-0.5 block font-sans text-xs text-[var(--color-foreground-3)]">
              {hint}
            </span>
          ) : null}
        </dd>
      </div>
    );
  },
);
KeyValue.displayName = 'KeyValue';

export { KeyValueList, KeyValue };
